import React, { useMemo } from 'react';
import { CategoryProductResult } from '../../../integrations/relewise/types/mapped';

export type PromoRenderItem =
    | { kind: 'product'; key: string; product: CategoryProductResult; index: number }
    | { kind: 'promo'; key: string; promo: React.ReactNode };

interface UsePromoBlocksProps {
    repeat?: boolean;
    startPosition?: number;
    frequency?: number;
    promoBlocks?: React.ReactNode[];
    products: CategoryProductResult[];
}

const getProductKey = (product: CategoryProductResult, index: number): string => {
    return product.variant?.relewiseVariantId ?? String(product.variant?.variantRecordId ?? index);
};

export default function usePromoBlocks({
    promoBlocks,
    startPosition,
    frequency,
    products,
    repeat
}: UsePromoBlocksProps) {
    const renderItems = useMemo<PromoRenderItem[]>(() => {
        const firstPromoPosition = startPosition ?? 0;
        const promoFrequency = frequency ?? 0;
        const totalPromoBlocks = Array.isArray(promoBlocks) ? promoBlocks.length : 0;

        const hasValidPromoConfig =
            Array.isArray(promoBlocks) && totalPromoBlocks > 0 && firstPromoPosition > 0 && promoFrequency > 0;

        let renderedPromoCount = 0;
        const items: PromoRenderItem[] = [];

        for (let index = 0; index < products.length; index += 1) {
            const product = products[index];
            const productKey = getProductKey(product, index);
            const positionInProducts = index + 1;

            items.push({
                kind: 'product',
                key: `product-${productKey}`,
                product,
                index
            });

            const shouldInsertPromoAfterThisProduct =
                hasValidPromoConfig &&
                positionInProducts >= firstPromoPosition &&
                (positionInProducts - firstPromoPosition) % promoFrequency === 0;

            if (!shouldInsertPromoAfterThisProduct) {
                continue;
            }

            if (repeat) {
                const promo = promoBlocks![renderedPromoCount % totalPromoBlocks];
                const promoKey = `promo-${renderedPromoCount}-after-${productKey}`;

                renderedPromoCount += 1;
                items.push({ kind: 'promo', key: promoKey, promo });
                continue;
            }

            if (renderedPromoCount < totalPromoBlocks) {
                const promo = promoBlocks![renderedPromoCount];
                const promoKey = `promo-${renderedPromoCount}-after-${productKey}`;

                renderedPromoCount += 1;
                items.push({ kind: 'promo', key: promoKey, promo });
            }
        }

        return items;
    }, [promoBlocks, startPosition, frequency, products, repeat]);

    return {
        renderItems
    };
}
